/**
 * @vuelto-surface
 * mobile: ok
 * desktop: n/a
 * strategy: mobile-only
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { MasterFilterSheetModal } from '../../../shared/filters/MasterFilterSheetModal';
import {
  AUDIT_PERIOD_PRESETS,
  type AuditCategoryFilter,
  type AuditPeriodPreset,
  type TeamHistoryFilters,
} from './useTeamHistoryFilters';
import { AUDIT_CATEGORY_LABELS, type AuditCategory } from '../../../shell/audit/auditCategories';

type UserOption = { id: string; name: string };

type Props = {
  filters: TeamHistoryFilters;
  userOptions: UserOption[];
};

const CATEGORY_OPTIONS: Array<{ id: AuditCategoryFilter; name: string }> = [
  { id: 'all', name: 'Todos' },
  ...(Object.keys(AUDIT_CATEGORY_LABELS) as AuditCategory[]).map((cat) => ({
    id: cat as AuditCategoryFilter,
    name: AUDIT_CATEGORY_LABELS[cat],
  })),
];

export function TeamHistoryMobileFilters({ filters, userOptions }: Props) {
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const [userSheetOpen, setUserSheetOpen] = useState(false);

  const categoryLabel = useMemo(
    () => CATEGORY_OPTIONS.find((c) => c.id === filters.category)?.name ?? 'Todos',
    [filters.category],
  );

  const periodLabel = useMemo(
    () => AUDIT_PERIOD_PRESETS.find((p) => p.id === filters.periodPreset)?.label ?? 'Hoy',
    [filters.periodPreset],
  );

  const userLabel = useMemo(() => {
    if (!filters.userFilter) return 'Todos';
    return userOptions.find((u) => u.id === filters.userFilter)?.name ?? 'Usuario';
  }, [filters.userFilter, userOptions]);

  const categoryActive = filters.category !== 'all';
  const periodActive = filters.periodPreset !== 'today';
  const userActive = filters.userFilter != null;

  return (
    <>
      <View style={styles.row}>
        <TouchableOpacity
          style={[styles.pill, categoryActive && styles.pillActive]}
          onPress={() => setCategorySheetOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`Categoría, ${categoryLabel}`}
        >
          <Text style={[styles.pillLabel, categoryActive && styles.pillLabelActive]} numberOfLines={1}>
            {categoryLabel}
          </Text>
          <Ionicons
            name="chevron-down"
            size={14}
            color={categoryActive ? ticketColors.gold : ticketColors.ink3}
          />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.pill, periodActive && styles.pillActive]}
          onPress={() => setPeriodSheetOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`Período, ${periodLabel}`}
        >
          <Text style={[styles.pillLabel, periodActive && styles.pillLabelActive]} numberOfLines={1}>
            {periodLabel}
          </Text>
          <Ionicons
            name="chevron-down"
            size={14}
            color={periodActive ? ticketColors.gold : ticketColors.ink3}
          />
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.pill, userActive && styles.pillActive]}
          onPress={() => setUserSheetOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`Usuario, ${userLabel}`}
        >
          <Text style={[styles.pillLabel, userActive && styles.pillLabelActive]} numberOfLines={1}>
            {userLabel}
          </Text>
          <Ionicons
            name="chevron-down"
            size={14}
            color={userActive ? ticketColors.gold : ticketColors.ink3}
          />
        </TouchableOpacity>
      </View>

      {filters.hasActiveFilters ? (
        <TouchableOpacity
          style={styles.clearBtn}
          onPress={filters.clearFilters}
          accessibilityRole="button"
          accessibilityLabel="Limpiar filtros"
        >
          <Ionicons name="close-circle" size={14} color={ticketColors.ink3} />
          <Text style={styles.clearBtnText}>Limpiar</Text>
        </TouchableOpacity>
      ) : null}

      <MasterFilterSheetModal
        visible={categorySheetOpen}
        title="Categoría"
        options={CATEGORY_OPTIONS}
        selectedId={filters.category}
        onSelect={(id) => {
          if (id) filters.setCategory(id as AuditCategoryFilter);
        }}
        onClose={() => setCategorySheetOpen(false)}
        allOptionLabel="Todos"
      />

      <MasterFilterSheetModal
        visible={periodSheetOpen}
        title="Período"
        options={AUDIT_PERIOD_PRESETS.map((p) => ({ id: p.id, name: p.label }))}
        selectedId={filters.periodPreset}
        onSelect={(id) => {
          if (id) filters.setPeriodPreset(id as AuditPeriodPreset);
        }}
        onClose={() => setPeriodSheetOpen(false)}
        showAllOption={false}
      />

      <MasterFilterSheetModal
        visible={userSheetOpen}
        title="Usuario"
        options={userOptions}
        selectedId={filters.userFilter}
        onSelect={(id) => filters.setUserFilter(id)}
        onClose={() => setUserSheetOpen(false)}
        allOptionLabel="Todos"
      />
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 6,
    paddingTop: spacing.xs,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 10,
    paddingVertical: 6,
    flexShrink: 1,
  },
  pillActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  pillLabel: {
    ...typography.caption,
    fontSize: 12,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  pillLabelActive: {
    color: ticketColors.gold,
    fontWeight: '700',
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    paddingVertical: 4,
  },
  clearBtnText: {
    ...typography.caption,
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '600',
  },
});
